<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';

const visible = ref(false);
const finishing = ref(false);
const progress = ref(0);

let progressTimer: ReturnType<typeof setInterval> | null = null;
let hideTimer: ReturnType<typeof setTimeout> | null = null;

const router = useRouter();
let removeBeforeEach: (() => void) | null = null;
let removeAfterEach: (() => void) | null = null;

function start(): void {
  // Clear any pending hide
  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
  if (progressTimer) {
    clearInterval(progressTimer);
    progressTimer = null;
  }

  finishing.value = false;
  progress.value = 15;
  visible.value = true;

  // Trickle: slow down as we approach 90%
  progressTimer = setInterval(() => {
    if (progress.value < 90) {
      // Increments shrink as progress grows
      const remaining = 90 - progress.value;
      progress.value += remaining * 0.1;
    }
  }, 200);
}

function finish(): void {
  if (progressTimer) {
    clearInterval(progressTimer);
    progressTimer = null;
  }

  progress.value = 100;
  finishing.value = true;

  // Fade out after reaching 100%
  hideTimer = setTimeout(() => {
    visible.value = false;
    finishing.value = false;
    progress.value = 0;
  }, 300);
}

onMounted(() => {
  removeBeforeEach = router.beforeEach((_to, _from, next) => {
    start();
    next();
  });

  removeAfterEach = router.afterEach(() => {
    finish();
  });
});

onUnmounted(() => {
  if (removeBeforeEach) removeBeforeEach();
  if (removeAfterEach) removeAfterEach();
  if (progressTimer) clearInterval(progressTimer);
  if (hideTimer) clearTimeout(hideTimer);
});
</script>

<template>
  <div
    v-if="visible"
    class="route-progress-bar"
    :class="{ 'route-progress-finishing': finishing }"
    role="progressbar"
    :aria-valuenow="Math.round(progress)"
    aria-valuemin="0"
    aria-valuemax="100"
  >
    <div
      class="route-progress-bar-fill"
      :style="{ width: `${progress}%` }"
    />
  </div>
</template>

<style scoped>
.route-progress-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  z-index: 9999;
  pointer-events: none;
}

.route-progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--color-accent), #f59e0b);
  transition: width 200ms ease;
  box-shadow: 0 0 8px rgba(217, 119, 6, 0.4);
}

.route-progress-finishing .route-progress-bar-fill {
  transition: width 150ms ease, opacity 300ms 150ms ease;
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .route-progress-bar-fill {
    transition: none;
  }
  .route-progress-finishing .route-progress-bar-fill {
    transition: none;
  }
}
</style>
